import { cardHeaderClasses, styled } from '@mui/material';

import type { ResourceColorPalette } from '../../../theme/tokens/resourcesColors';
import { Card } from '../Card';
import { CardContent } from '../CardContent';
import { CardHeader } from '../CardHeader';
import { type CardResourceProps, useCardResource } from './useCardResource';

type Props = CardResourceProps;

type CardResourceOwnerState = {
  resourceColor: ResourceColorPalette;
  fullHeight: boolean;
};

const CardResourceRoot = styled(Card)<{ ownerState: CardResourceOwnerState }>(
  ({ theme, ownerState }) => ({
    overflow: 'hidden',
    ...(ownerState.fullHeight && { height: '100%' }),
    [`& .${cardHeaderClasses.root}`]: {
      backgroundColor: ownerState.resourceColor.light,
    },
    [`& .${cardHeaderClasses.title}`]: {
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.text.primary,
      lineHeight: 1.2,
    },
    [`& .${cardHeaderClasses.subheader}`]: {
      color: theme.palette.text.secondary,
    },
  }),
);

export function CardResource(props: Props) {
  const hook = useCardResource(props);

  const hasHeader = props.title !== undefined
    || props.subheader !== undefined
    || props.action !== undefined
    || props.avatar !== undefined;

  const {
    variant: _variant,
    title: _title,
    subheader: _subheader,
    action: _action,
    avatar: _avatar,
    children: _children,
    disableCardContent: _disableCardContent,
    fullHeight: _fullHeight,
    ...restCardProps
  } = props;

  return (
    <CardResourceRoot
      {...restCardProps}
      ownerState={{ resourceColor: hook.resourceColor, fullHeight: props.fullHeight ?? false }}
    >
      {hasHeader ? (
        <CardHeader
          avatar={hook.avatar}
          title={props.title}
          subheader={props.subheader}
          action={props.action}
        />
      ) : null}
      {props.children !== undefined ? (
        hasHeader && !props.disableCardContent ? (
          <CardContent>{props.children}</CardContent>
        ) : (
          props.children
        )
      ) : null}
    </CardResourceRoot>
  );
}

export type { Props as CardResourceProps };
